import React, { useState, useEffect } from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import SmsProviderBalancePanel from '../SmsProviderBalancePanel/SmsProviderBalancePanel';
import { PageShell } from '../ui';
import './AdminMobileMonitoring.css';

const AdminMobileMonitoring = ({ user }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [systemMetrics, setSystemMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const API_KEY = 'IJf238emdc8f247rcjdcwe823d8*EDwfefwcwey2eYSDYDFWN';

  const fetchDashboard = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [dashboardResponse, systemResponse] = await Promise.all([
        fetch('/api/monitoring/dashboard', {
          headers: {
            'Authorization': `Bearer ${API_KEY}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache'
          }
        }),
        fetch('/api/monitoring/system-detailed', {
          headers: {
            'Authorization': `Bearer ${API_KEY}`,
            'Content-Type': 'application/json'
          }
        })
      ]);
      
      if (dashboardResponse.ok && systemResponse.ok) {
        const dashboardData = await dashboardResponse.json();
        const systemData = await systemResponse.json();
        
        setDashboardData(dashboardData.data);
        setSystemMetrics(systemData.data);
        setLastUpdate(new Date().toLocaleTimeString());
      } else {
        setError(`Ошибка загрузки данных`);
      }
    } catch (err) {
      setError(`Ошибка сети: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    const interval = setInterval(fetchDashboard, 30000); // Обновление каждые 30 секунд
    return () => clearInterval(interval);
  }, []);

  const formatUptime = (uptime) => {
    const days = Math.floor(uptime / 86400);
    const hours = Math.floor((uptime % 86400) / 3600);
    const minutes = Math.floor((uptime % 3600) / 60);
    
    if (days > 0) return `${days}д ${hours}ч`;
    if (hours > 0) return `${hours}ч ${minutes}м`;
    return `${minutes}м`;
  };

  const formatBytes = (bytes) => {
    return `${Math.round(bytes / 1024 / 1024 / 1024)} GB`;
  };

  if (error) {
    return (
      <PageShell>
        <div className="mobile-monitoring">
          <div className="error-card">
            <h2>❌ Ошибка загрузки</h2>
            <p>{error}</p>
            <button onClick={fetchDashboard} className="retry-btn">
              🔄 Попробовать снова
            </button>
          </div>
        </div>
      </PageShell>
    );
  }

  if (!dashboardData || !systemMetrics) {
    return (
      <PageShell>
        <div className="mobile-monitoring">
          <div className="loading-card">
            <div className="spinner"></div>
            <h2>⏳ Загрузка данных...</h2>
          </div>
        </div>
      </PageShell>
    );
  }

  const { overview } = dashboardData;

  // Функция для определения статуса метрики
  const getMetricStatus = (value, thresholds) => {
    if (value >= thresholds.critical) return 'critical';
    if (value >= thresholds.warning) return 'warning';
    return 'good';
  };

  return (
    <PageShell>
    <div className="mobile-monitoring">
      {/* Header */}
      <div className="header-card">
        <h1>🖥️ System Monitor</h1>
        <p>Мониторинг производительности сервера</p>
        <div className="last-update">
          Обновлено: {lastUpdate}
          {loading && <span className="updating">🔄</span>}
        </div>
      </div>

      <div className="monitoring-grid">
        <div className="system-section monitoring-grid-full">
          <SmsProviderBalancePanel user={user} />
        </div>

        {/* Detailed System Info */}
        <div className="system-section monitoring-grid-full">
          <h2>💻 Подробная статистика системы</h2>
          
          {/* Key Metrics */}
          <div className="metrics-grid">
            <div className={`metric-box ${getMetricStatus(systemMetrics.cpu.usage, { warning: 70, critical: 85 })}`}>
              <div className="metric-box-value">{systemMetrics.cpu.usage.toFixed(1)}%</div>
              <div className="metric-box-label">CPU Load</div>
            </div>
            
            <div className={`metric-box ${getMetricStatus(systemMetrics.memory.percentage, { warning: 70, critical: 85 })}`}>
              <div className="metric-box-value">{systemMetrics.memory.percentage}%</div>
              <div className="metric-box-label">RAM Usage</div>
            </div>
            
            <div className={`metric-box ${getMetricStatus(systemMetrics.disk.percentage, { warning: 80, critical: 90 })}`}>
              <div className="metric-box-value">{systemMetrics.disk.percentage}%</div>
              <div className="metric-box-label">Disk Usage</div>
            </div>
            
            <div className="metric-box good">
              <div className="metric-box-value">{overview.activeUsers || 0}</div>
              <div className="metric-box-label">Active Users</div>
            </div>
            
            <div className="metric-box good">
              <div className="metric-box-value">{systemMetrics.processes}</div>
              <div className="metric-box-label">Running Processes</div>
            </div>
            
            <div className="metric-box good">
              <div className="metric-box-value">{formatUptime(systemMetrics.uptime)}</div>
              <div className="metric-box-label">System Uptime</div>
            </div>
          </div>
          
          {/* Memory Chart */}
          <div className="chart-container">
            <h3>📊 Memory Usage</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Used', value: systemMetrics.memory.used, color: '#22c55e' },
                    { name: 'Free', value: systemMetrics.memory.free, color: '#4ade80' },
                  ]}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                >
                  {[{ color: '#22c55e' }, { color: '#4ade80' }].map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatBytes(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          {/* CPU Load Average */}
          <div className="chart-container">
            <h3>⚡ CPU Load Average</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={[
                { name: '1 min', load: systemMetrics.cpu.loadAvg[0] },
                { name: '5 min', load: systemMetrics.cpu.loadAvg[1] },
                { name: '15 min', load: systemMetrics.cpu.loadAvg[2] },
              ]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="load" fill="#22c55e" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          {/* Services Status */}
          <div>
            <h3>🔧 Services Status</h3>
            <div className="services-grid">
              {Object.entries(systemMetrics.services).map(([service, status]) => {
                // Красивые названия для сервисов
                const serviceNames = {
                  'nginx': 'Nginx Web Server',
                  'mongod': 'MongoDB Database',
                  'redis-server': 'Redis Cache',
                  'redis': 'Redis Cache',
                  'node-app': 'Node.js App'
                };
                
                return (
                  <div key={service} className="service-status">
                    <span className="service-name">{serviceNames[service] || service}</span>
                    <span className={`service-indicator ${status}`}></span>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Network Stats */}
          <div className="metrics-grid">
            <div className="metric-box good">
              <div className="metric-box-value">{formatBytes(systemMetrics.network.rx)}</div>
              <div className="metric-box-label">Network RX</div>
            </div>
            <div className="metric-box good">
              <div className="metric-box-value">{formatBytes(systemMetrics.network.tx)}</div>
              <div className="metric-box-label">Network TX</div>
            </div>
          </div>
        </div>
      </div>

      {/* Refresh Button */}
      <div className="refresh-section">
        <button 
          onClick={fetchDashboard} 
          className="refresh-btn"
          disabled={loading}
        >
          {loading ? '🔄 Обновление...' : '🔄 Обновить данные'}
        </button>
      </div>
    </div>
    </PageShell>
  );
};

export default AdminMobileMonitoring; 